import { useState, useEffect, useCallback } from 'react';
import { FiSliders, FiX, FiSearch } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import EmptyState from '../components/common/EmptyState';
import { adminEntitlementsAPI, usersAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useUI } from '../context/UIContext';

const TEAL = '#145f5f';
const MUTE = '#7a7060';
const BORDER = '#ddd6c6';
const CACHE_KEY = 'admin:entitlements';

const FEATURES = [
  { key: 'chat', label: 'AI Chat' },
  { key: 'drafting', label: 'AI Drafting' },
  { key: 'agent', label: 'AI Actions (agent)' },
];
const QUOTAS = [
  { key: 'dailyAITokens', label: 'AI tokens / day', hint: 'blank = unlimited · 0 = blocked' },
  { key: 'monthlyOrders', label: 'Orders / month', hint: 'blank = unlimited' },
  { key: 'maxProfessionals', label: 'Max professionals', hint: 'corporates only' },
  { key: 'maxCompanies', label: 'Max companies', hint: 'owned or created' },
];
const PERSONA_LABEL = { professional: 'CA/CS Professionals', corporate: 'Corporates' };

// Editor for one entitlement row (persona default or user override).
// Feature value model: true/false explicit, null = inherit (rendered as the
// inherited/default state with an "inherit" tag).
function EntitlementEditor({ initial, onSave, saving, compact }) {
  const [features, setFeatures] = useState({});
  const [quotas, setQuotas] = useState({});

  useEffect(() => {
    setFeatures(initial?.features || {});
    const q = {};
    for (const { key } of QUOTAS) {
      const v = initial?.quotas?.[key];
      q[key] = v == null ? '' : String(v);
    }
    setQuotas(q);
  }, [initial]);

  const cycleFeature = (key) => {
    // click cycles: inherit(null) → off(false) → on(true) → inherit
    setFeatures((prev) => {
      const v = prev?.[key];
      const next = v == null ? false : v === false ? true : null;
      return { ...prev, [key]: next };
    });
  };

  const handleSave = () => {
    const quotaPatch = {};
    for (const { key } of QUOTAS) {
      const raw = String(quotas[key] ?? '').trim();
      quotaPatch[key] = raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0);
    }
    onSave({ features, quotas: quotaPatch });
  };

  const featureChip = (key) => {
    const v = features?.[key];
    const state = v === false ? 'off' : v === true ? 'on' : 'inherit';
    const styles = {
      on: { background: '#e4efe9', color: '#2f6b4f', border: '1px solid #bcd9c8' },
      off: { background: '#f7e7e4', color: '#9a3a3a', border: '1px solid #eac5bd' },
      inherit: { background: '#f0ece2', color: MUTE, border: `1px solid ${BORDER}` },
    }[state];
    return (
      <button
        type="button"
        onClick={() => cycleFeature(key)}
        className="px-2.5 py-[4px] rounded-full text-[10.5px] font-semibold uppercase tracking-wide transition-all active:scale-95"
        style={styles}
        title="Click to cycle: inherit → off → on"
      >
        {state === 'inherit' ? 'inherit' : state}
      </button>
    );
  };

  return (
    <div className={compact ? '' : 'mt-1'}>
      <div className="flex flex-col gap-2 mb-3">
        {FEATURES.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-[#08111f]">{f.label}</span>
            {featureChip(f.key)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {QUOTAS.map((q) => (
          <label key={q.key} className="flex flex-col gap-1">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold" style={{ color: MUTE }}>{q.label}</span>
            <input
              type="number"
              min="0"
              placeholder="unlimited"
              value={quotas[q.key] ?? ''}
              onChange={(e) => setQuotas((prev) => ({ ...prev, [q.key]: e.target.value }))}
              className="border rounded-[9px] px-2.5 py-[7px] text-[12px] outline-none focus:border-[#145f5f] transition-colors"
              style={{ borderColor: BORDER }}
            />
            <span className="text-[9.5px]" style={{ color: '#a39a88' }}>{q.hint}</span>
          </label>
        ))}
      </div>
      <Button size="sm" loading={saving} onClick={handleSave}>Save</Button>
    </div>
  );
}

// Admin Entitlements — persona defaults (professional / corporate) plus
// per-user overrides. Everything inherits: user → persona → built-in
// (all features on, all quotas unlimited), so blank rows change nothing.
export default function AdminEntitlementsPage() {
  const { showToast } = useUI();
  // Stale-while-revalidate: this route unmounts on nav, so the persona editors
  // reset to blank and repopulated on every return. Cache the payload
  // module-side (services/viewCache, CACHE_KEY) and render it instantly on revisit.
  const [data, setData] = useState(() => getView(CACHE_KEY) ?? null);
  const [loading, setLoading] = useState(() => !hasView(CACHE_KEY));
  const [savingKey, setSavingKey] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!hasView(CACHE_KEY)) setLoading(true);
    try {
      const res = await adminEntitlementsAPI.getAll();
      if (res.success) { setData(res.data); setView(CACHE_KEY, res.data); }
    } catch (err) {
      // Keep the cached payload on a failed refresh; toast only on a first load.
      console.error('Error fetching entitlements:', err);
      if (!hasView(CACHE_KEY)) showToast('Failed to load entitlements', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const savePersona = async (persona, patch) => {
    setSavingKey(`persona:${persona}`);
    try {
      const res = await adminEntitlementsAPI.updatePersona(persona, patch);
      if (res.success) { showToast(`${PERSONA_LABEL[persona]} defaults saved`); fetchAll(); }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally { setSavingKey(''); }
  };

  const saveUser = async (userId, patch) => {
    setSavingKey(`user:${userId}`);
    try {
      const res = await adminEntitlementsAPI.updateUser(userId, patch);
      if (res.success) { showToast('User override saved'); setEditingUserId(null); setSearchResults([]); setSearch(''); fetchAll(); }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally { setSavingKey(''); }
  };

  const clearUser = async (userId) => {
    try {
      const res = await adminEntitlementsAPI.clearUser(userId);
      if (res.success) { showToast('Override removed — persona default applies'); fetchAll(); }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const res = await usersAPI.getAll({ search: search.trim(), limit: 8 });
      const rows = (res.data?.users || []).filter((u) => !['admin', 'super_admin'].includes(u.roleName));
      setSearchResults(rows);
      if (!rows.length) showToast('No matching non-admin users', 'info');
    } catch (err) {
      showToast(`Search failed: ${err.message}`, 'error');
    } finally { setSearching(false); }
  };

  const personas = data?.personas || {};
  const overrides = data?.overrides || [];

  return (
    <AppLayout>
      <PageHeader
        title="Entitlements"
        subtitle="Per-persona feature access and usage quotas, with per-user overrides. Blank = unlimited; everything inherits down: user → persona → built-in."
      />

      {/* Persona defaults */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {Object.keys(PERSONA_LABEL).map((p) => (
          <Card key={p}>
            <div className="flex items-center gap-2 mb-3">
              <FiSliders size={14} style={{ color: TEAL }} />
              <span className="text-[13px] font-bold text-[#08111f]">{PERSONA_LABEL[p]}</span>
              <span className="text-[10.5px] px-2 py-[2px] rounded-full" style={{ background: '#f0ece2', color: MUTE }}>persona default</span>
            </div>
            <EntitlementEditor
              initial={personas[p]}
              saving={savingKey === `persona:${p}`}
              onSave={(patch) => savePersona(p, patch)}
            />
          </Card>
        ))}
      </div>

      {/* Per-user overrides */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <span className="text-[13px] font-bold text-[#08111f]">Per-user overrides</span>
          <form onSubmit={runSearch} className="flex items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px]" style={{ color: MUTE }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find user by name or email…"
                className="border rounded-[9px] pl-7 pr-2.5 py-[7px] text-[12px] outline-none focus:border-[#145f5f] w-[230px] transition-colors"
                style={{ borderColor: BORDER }}
              />
            </div>
            <Button size="sm" variant="outline" type="submit" loading={searching}>Search</Button>
          </form>
        </div>

        {searchResults.length > 0 && (
          <div className="mb-4 border rounded-[10px] divide-y" style={{ borderColor: BORDER }}>
            {searchResults.map((u) => (
              <div key={u._id} className="px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-[12px] font-semibold text-[#08111f]">{u.fullName}</span>
                    <span className="text-[11px] ml-2" style={{ color: MUTE }}>{u.email} · {u.roleName}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setEditingUserId(editingUserId === u._id ? null : u._id)}>
                    {editingUserId === u._id ? 'Cancel' : 'Set override'}
                  </Button>
                </div>
                {editingUserId === u._id && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f3eee2' }}>
                    <EntitlementEditor
                      compact
                      initial={overrides.find((o) => String(o.user) === String(u._id))}
                      saving={savingKey === `user:${u._id}`}
                      onSave={(patch) => saveUser(u._id, patch)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && overrides.length === 0 ? (
          <EmptyState
            icon={FiSliders}
            tone={TEAL}
            title="No user overrides"
            subtitle="Everyone follows their persona default. Search for a user above to give them a custom limit or feature set."
            variant="filter"
          />
        ) : (
          <div className="flex flex-col divide-y" style={{ borderColor: '#f3eee2' }}>
            {overrides.map((o) => (
              <div key={o._id} className="py-2.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <span className="text-[12px] font-semibold text-[#08111f]">{o.userInfo?.fullName || 'Unknown user'}</span>
                    <span className="text-[11px] ml-2" style={{ color: MUTE }}>
                      {o.userInfo?.email} · {o.userInfo?.roleName}
                      {o.expiresAt && ` · expires ${new Date(o.expiresAt).toLocaleDateString('en-IN')}`}
                    </span>
                    <div className="text-[10.5px] mt-0.5" style={{ color: MUTE }}>
                      {FEATURES.filter((f) => o.features?.[f.key] === false).map((f) => `${f.label} off`).join(' · ') || null}
                      {FEATURES.some((f) => o.features?.[f.key] === false) && QUOTAS.some((q) => o.quotas?.[q.key] != null) ? ' · ' : ''}
                      {QUOTAS.filter((q) => o.quotas?.[q.key] != null).map((q) => `${q.label}: ${o.quotas[q.key]}`).join(' · ') || (FEATURES.some((f) => o.features?.[f.key] === false) ? '' : 'no explicit limits')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setEditingUserId(editingUserId === String(o.user) ? null : String(o.user))}>
                      {editingUserId === String(o.user) ? 'Cancel' : 'Edit'}
                    </Button>
                    <Button size="sm" variant="danger" leftIcon={<FiX size={12} />} onClick={() => clearUser(o.user)}>Remove</Button>
                  </div>
                </div>
                {editingUserId === String(o.user) && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f3eee2' }}>
                    <EntitlementEditor
                      compact
                      initial={o}
                      saving={savingKey === `user:${o.user}`}
                      onSave={(patch) => saveUser(o.user, patch)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </AppLayout>
  );
}
